import { BaseModel } from './base.model';
import { isValidEmail, JsonObject } from '../helpers';
import { object, string } from 'yup';

type UserExtRef = {
  workos: {
    workosUserId: string;
    workosOrganizationId: string | null;
    workosDirectoryId: string;
    workosCreatedAt: string;
    workosUpdatedAt: string;
  };
};

export class UserModel extends BaseModel {
  orgId: string;
  name: string;
  email: string;
  groups: string[];
  externalReferences: UserExtRef;
  lastSigninTimestamp?: number;
  connectorId?: string;

  // name is only required when creating a new user (no id); existing users may have
  // empty names due to SCIM sync omitting the field
  schema = object({
    name: string().test('name-required-for-new', 'Please enter a name', (value, context) => {
      const parent = context.parent as { id?: string };
      return !!parent.id || !!value;
    }),
    email: string()
      .test('email', 'Please enter a valid email address', isValidEmail)
      .required('Please enter an email address'),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof UserModel, unknown>;

    this.orgId = (data.orgId as string) || '';
    this.name = (data.name as string) || '';
    this.email = (data.email as string) || '';
    this.groups = (data.groups as string[]) || [];

    this.externalReferences = (data.externalReferences as UserExtRef) || {
      workos: {
        workosUserId: '',
        workosOrganizationId: null,
        workosDirectoryId: '',
        workosCreatedAt: '',
        workosUpdatedAt: '',
      },
    };

    if (data.lastSigninTimestamp) {
      this.lastSigninTimestamp = data.lastSigninTimestamp as number;
    }

    if (data.connectorId) {
      this.connectorId = data.connectorId as string;
    }
  }

  get source(): string {
    if (this.externalReferences?.workos?.workosDirectoryId) {
      return 'sync';
    }

    if (this.hasLogin) {
      return 'login';
    }

    if (this.connectorId) {
      return 'connector';
    }

    return 'f5-wais';
  }

  get isSourceSystem(): boolean {
    return this.source === 'f5-wais';
  }

  get sourceLabel(): string {
    switch (this.source) {
      case 'sync':
        return 'Sync';
      case 'login':
        return 'Login';
      case 'connector':
        return 'Connector';
      default:
        return 'F5-WAIS';
    }
  }

  get isExternal(): boolean {
    return !!this.externalReferences?.workos?.workosDirectoryId;
  }

  get hasLogin(): boolean {
    return !!this.lastSigninTimestamp;
  }

  get isConnectorUser(): boolean {
    return this.source === 'connector';
  }

  get canDelete(): boolean {
    return !this.isExternal && !this.hasLogin && !this.isConnectorUser;
  }
}
